// src/App.tsx
import React, { useEffect, useRef } from "react"; // Import useRef
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RoomEntry } from "./components/room-entry"; // Ensure correct path
import { YoutubeLayout } from "./components/youtube-layout"; // Ensure correct path
import { StandardLayout } from "./components/standard-layout"; // Ensure correct path
import { useRoom } from "./hooks/use-room"; // Ensure correct path
import { useChatWebSocket } from "./hooks/use-chat-websocket"; // Ensure correct path
import { usePageFocusAndTitle } from "./hooks/use-page-title-and-focus"; // Import the new hook

// Create a client
const queryClient = new QueryClient();

function AppContent() {
  const {
    roomId,
    isValidRoom,
    isYoutubeRoom,
    youtubeVideoId,
    isLoading: isLoadingRoom,
  } = useRoom();

  const {
    messages,
    sendMessage,
    connectionStatus,
    chatError,
    readyState,
  } = useChatWebSocket(roomId, isValidRoom); // Enable WebSocket only for valid rooms

  // --- Add Page Focus and Title Hook ---
  // Use a dynamic base title including the roomId if available
  const { notifyMessageArrived } = usePageFocusAndTitle(
    roomId ? `FST Chat: ${roomId}` : "FST Chat"
  );

  // Ref to store the previous messages count
  const prevMessagesLengthRef = useRef(messages.length);

  // Effect to detect new messages and notify the title hook
  useEffect(() => {
    // Check if the number of messages has increased since the last render
    if (messages.length > prevMessagesLengthRef.current) {
      // A new message has arrived
      notifyMessageArrived();
    }
    // Update the ref with the current messages length for the next comparison
    prevMessagesLengthRef.current = messages.length;
  }, [messages, notifyMessageArrived]); // Depend on messages and the notification function

  // Handle redirect for invalid room IDs present in the URL
  useEffect(() => {
    if (!isLoadingRoom && roomId && !isValidRoom) {
      console.log("Redirecting due to invalid room ID:", roomId);
      if (typeof window !== "undefined") {
        window.location.pathname = "/";
      }
    }
  }, [isLoadingRoom, roomId, isValidRoom]);

  // Display loading or handle redirect state
  if (isLoadingRoom) {
    return (
      <div className="flex h-dvh w-full items-center justify-center">
        Loading...
      </div>
    ); // Or a proper loading spinner
  }

  // If redirecting, render null
  if (roomId && !isValidRoom) {
    return null;
  }

  // --- Render Logic ---

  if (!isValidRoom) {
    // Render RoomEntry if no valid room ID is determined (includes initial state)
    return <RoomEntry />;
  }

  // We have a valid room ID, decide layout based on YouTube status
  if (isYoutubeRoom && youtubeVideoId) {
    return (
      <YoutubeLayout
        roomId={roomId!} // roomId is guaranteed non-null here
        youtubeVideoId={youtubeVideoId}
        messages={messages}
        sendMessage={sendMessage}
        connectionStatus={connectionStatus}
        chatError={chatError}
        readyState={readyState}
      />
    );
  } else {
    return (
      <StandardLayout
        roomId={roomId!} // roomId is guaranteed non-null here
        messages={messages}
        sendMessage={sendMessage}
        connectionStatus={connectionStatus}
        chatError={chatError}
        readyState={readyState}
      />
    );
  }
}

// Wrap the app with QueryClientProvider
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
}

export default App;
